import { Dropdown } from "Common/Components/Dropdown";
import { useNotifications } from "hooks/useNotifications";
import { BellRing, CheckCircle2 } from "lucide-react";
import React from "react";
import SimpleBar from "simplebar-react";
import { AppNotification } from "slices/notifications/types";
import { getIconComponent } from "utils/iconUtils";

const NotificationDropdown: React.FC = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  const hasNotifications = notifications.length > 0;
  return (
    <Dropdown className="relative flex items-center h-header">
      <Dropdown.Trigger
        type="button"
        className="inline-flex justify-center relative items-center p-0 text-topbar-item transition-all size-[37.5px] duration-200 ease-linear bg-topbar rounded-md dropdown-toggle btn hover:bg-topbar-item-bg-hover hover:text-topbar-item-hover group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[topbar=dark]:dark:text-zink-200 group-data-[topbar=dark]:text-topbar-item-dark"
        id="notificationDropdown"
        data-bs-toggle="dropdown"
      >
        <BellRing className="inline-block size-5 stroke-1 fill-slate-100 group-data-[topbar=dark]:fill-topbar-item-bg-hover-dark group-data-[topbar=brand]:fill-topbar-item-bg-hover-brand" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex w-1.5 h-1.5">
            <span className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping bg-sky-400" />
            <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-sky-500" />
          </span>
        )}
      </Dropdown.Trigger>
      <Dropdown.Content
        placement="right-end"
        className="absolute z-50 ltr:text-left rtl:text-right bg-white rounded-md shadow-md !top-4 dropdown-menu min-w-[20rem] lg:min-w-[26rem] dark:bg-zink-600"
        aria-labelledby="notificationDropdown"
      >
        <div className="p-4">
          <div className="flex items-center justify-between">
            <h6 className="mb-4 text-16">
              Notificações{" "}
              {notifications.length > 0 && (
                <span className="inline-flex items-center justify-center size-5 ml-1 text-[11px] font-medium border rounded-full text-white bg-orange-500 border-orange-500">
                  {notifications.length}
                </span>
              )}
            </h6>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllAsRead}
                className="text-xs text-custom-500 hover:text-custom-600 focus:outline-none"
              >
                Limpar todas
              </button>
            )}
          </div>
        </div>
        <SimpleBar className="max-h-[350px]">
          {hasNotifications ? (
            <div className="flex flex-col gap-1">
              {notifications.map((item: AppNotification) => (
                <div
                  key={item.id}
                  className={`flex gap-3 p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-zink-500 ${
                    item.isRead ? "" : "bg-blue-50 dark:bg-blue-900/20"
                  }`}
                >
                  <div className="flex items-center justify-center rounded-md size-12 bg-violet-500/20 text-violet-500 shrink-0">
                    {item.iconName &&
                      React.createElement(getIconComponent(item.iconName) || BellRing, {
                        className: "size-5",
                      })}
                  </div>
                  <div className="grow">
                    <h6 className="mb-1 font-medium ltr:text-15 rtl:text-16">{item.title}</h6>
                    <p className="mb-2 text-sm text-slate-500 dark:text-zink-300">{item.message}</p>
                    <p className="mb-0 text-xs text-slate-400 dark:text-zink-200">
                      <span className="align-middle">{item.timestamp}</span>
                    </p>
                  </div>
                  {item.isRead ? null : (
                    <div className="flex items-start gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => markAsRead(item.id)}
                        className="p-1 border-0 btn bg-white/25 text-slate-400 btn-sm hover:bg-green-500 hover:text-white focus:bg-green-500 focus:text-white focus:outline-none"
                        title="Remover notificação"
                      >
                        <CheckCircle2 className="size-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-slate-500 dark:text-zink-300">
              <BellRing className="mx-auto mb-4 size-12 text-slate-400" />
              <p>Nenhuma notificação</p>
            </div>
          )}
        </SimpleBar>
      </Dropdown.Content>
    </Dropdown>
  );
};

export default NotificationDropdown;
